import { EnvironmentInjector, inject, Injectable, Type } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Polygon } from 'geojson';

import { ProjectColors } from '@connect-services/client/components/color-dropdown/color-dropdown.component';
import {
    CreateEditProjectModalComponent,
    ProjectModalCloseMode,
    ProjectShapingModalResult,
} from '@connect-services/client/components/create-edit-project-modal/create-edit-project-modal.component';
import { MapComponent } from '@connect-services/client/components/map/map.component';
import { Project } from '@connect-services/client/sdk/api';
import { MapBboxShapingService } from '@connect-services/client/services/map/bbox-shaping/map-bbox-shaping.service';
import { MapModeService } from '@connect-services/client/services/map/mode/map-mode.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { CreateProjectShapingStateService } from '@connect-services/client/services/project/shaping/shaping-state/create-project-shaping-state.service';
import {
    ProjectDefaultData,
    ShapingProjectState,
} from '@connect-services/client/services/project/shaping/shaping-state/shaping-project-state';

export class ProjectCreationEditionForm extends FormGroup<{
    bbox: FormControl<Polygon | null>;
    color: FormControl<ProjectColors>;
    name: FormControl<string>;
    projection: FormControl<Project.ProjectionEnum>;
}> {}

@Injectable({
    providedIn: 'root',
})
export class ShapingProjectService {
    private readonly injector = inject(EnvironmentInjector);
    private readonly mapBboxShapingService = inject(MapBboxShapingService);
    private readonly mapModeService = inject(MapModeService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly matDialog = inject(MatDialog);
    private state?: ShapingProjectState;

    public get currentState(): Type<ShapingProjectState> | undefined {
        return this.state?.constructor as Type<ShapingProjectState>;
    }

    public get projectForm(): ProjectCreationEditionForm | undefined {
        return this.state?.form;
    }

    public initBboxShaping(): void {
        if (this.state instanceof CreateProjectShapingStateService) {
            this.mapSourceService.clearSources(MapComponent.backgroundSource(), []);
        }

        this.mapModeService.isBboxShapingActive = true;
        const existingBbox = this.state!.form.controls.bbox.value;

        this.mapBboxShapingService.initMap({
            initialBounds: existingBbox
                ? {
                      maxLat: existingBbox.coordinates[0][2][1],
                      maxLng: existingBbox.coordinates[0][2][0],
                      minLat: existingBbox.coordinates[0][0][1],
                      minLng: existingBbox.coordinates[0][0][0],
                  }
                : null,
            onClose: (bbox) => {
                const data = {
                    ...this.state!.getDefaultData(),
                    bbox: bbox,
                };

                this.state!.setDefaultData(data);
            },
        });
    }

    public openShapingProjectModal<T extends ShapingProjectState>(state: Type<T>): void {
        this.setState(state);

        const dialogRef = this.matDialog.open(CreateEditProjectModalComponent, {
            autoFocus: false,
            data: { title: this.state?.title, ...(this.state?.getDefaultData() ?? {}) },
            width: '40vw',
        });

        dialogRef.afterClosed().subscribe(async (result: ProjectShapingModalResult | null) => {
            if (!result) {
                this.clearState();

                return;
            }

            if (result.mode === ProjectModalCloseMode.BBOX_SHAPING) {
                const data = {
                    bbox: this.state!.form.controls.bbox.value,
                    color: this.state!.form.controls.color.value,
                    name: this.state!.form.controls.name.value,
                    projection: this.state!.form.controls.projection.value,
                };
                this.state!.setDefaultData(data);

                this.initBboxShaping();

                return;
            }

            if (!result.creationEditionForm || result.creationEditionForm.invalid) {
                this.clearState();

                return;
            }

            await this.state?.onConfirm(result.creationEditionForm);
            this.clearState();
        });
    }

    public setDefaultData(data: ProjectDefaultData): void {
        this.state?.setDefaultData(data);
    }

    private clearState(): void {
        this.state?.clear();
        this.state = undefined;
    }

    private setState<T extends ShapingProjectState>(state: Type<T>): void {
        this.state = this.injector.get(state);
    }
}
